import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { header2Styles, paragraphStyles } from "@consts";
import {
  GLOSSARY_GROUPS,
  glossaryGroupFromHash,
  glossaryGroupHref,
  glossaryTermsInGroup,
  type GlossaryGroup,
  type GlossaryTerm,
} from "../../content/siteCatalog";

function termMatches(term: GlossaryTerm, query: string): boolean {
  if (!query) return true;
  const hay = `${term.term} ${term.meaning}`.toLowerCase();
  return hay.includes(query);
}

function termIdFromHash(hash: string): string | null {
  const value = hash.replace(/^#/, "");
  if (!value.startsWith("term-")) return null;
  return value.slice("term-".length);
}

function GroupPanel({
  group,
  query,
  open,
  onToggle,
  highlightedTermId,
}: {
  group: GlossaryGroup;
  query: string;
  open: boolean;
  onToggle: () => void;
  highlightedTermId: string | null;
}) {
  const terms = glossaryTermsInGroup(group).filter((term) =>
    termMatches(term, query),
  );
  if (query && terms.length === 0) return null;

  return (
    <section
      id={`glossary-${group.id}`}
      className="panel scroll-mt-48 overflow-hidden"
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`glossary-${group.id}-panel`}
        onClick={onToggle}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-accent/5"
      >
        <span className="mt-0.5 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[11px] font-semibold text-accent">
          {group.tag}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium text-ink">{group.title}</span>
            <span className="text-xs text-faint">
              {terms.length} {terms.length === 1 ? "label" : "labels"}
              <span className="ml-2 font-semibold text-accent">
                {open ? "Hide" : "Show"}
              </span>
            </span>
          </span>
          <span className="mt-1 block text-sm text-muted">{group.summary}</span>
        </span>
      </button>

      {open ? (
        <div id={`glossary-${group.id}-panel`} className="border-t border-line/20 px-4 pb-4">
          <p className="pt-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-faint">
            Used on
            {group.usedOn.map((link) => (
              <Link
                key={link.to + link.label}
                to={link.to}
                className="ml-2 inline-flex rounded-full border border-line/35 px-2 py-0.5 font-semibold normal-case tracking-normal text-info hover:border-info/50"
              >
                {link.label}
              </Link>
            ))}
          </p>
          <dl className="mt-3 space-y-3">
            {terms.map((item) => {
              const highlighted = highlightedTermId === item.id;
              return (
                <div
                  key={item.id}
                  id={`term-${item.id}`}
                  className={[
                    "scroll-mt-48 rounded-lg border px-3 py-2.5 transition",
                    highlighted
                      ? "border-info bg-info/15 ring-2 ring-info/50"
                      : "border-line/20 bg-bg/40",
                  ].join(" ")}
                >
                  <dt
                    className={[
                      "text-sm font-semibold",
                      highlighted ? "text-info" : "text-info/90",
                    ].join(" ")}
                  >
                    {item.term}
                  </dt>
                  <dd className="mt-1 text-sm leading-relaxed text-muted">
                    {item.meaning}
                  </dd>
                  {item.references?.length ? (
                    <dd className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
                      <span className="font-semibold uppercase tracking-[0.12em] text-faint">
                        Sources
                      </span>
                      {item.references.map((reference) => (
                        <a
                          key={reference.url}
                          href={reference.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-info hover:underline"
                        >
                          {reference.label}
                        </a>
                      ))}
                    </dd>
                  ) : null}
                </div>
              );
            })}
          </dl>
        </div>
      ) : null}
    </section>
  );
}

export function GlossarySection() {
  const [query, setQuery] = useState("");
  const [openGroup, setOpenGroup] = useState<string | null>(() =>
    typeof window === "undefined"
      ? null
      : glossaryGroupFromHash(window.location.hash),
  );
  const [highlightedTermId, setHighlightedTermId] = useState<string | null>(
    () =>
      typeof window === "undefined"
        ? null
        : termIdFromHash(window.location.hash),
  );
  const initialTermId =
    typeof window === "undefined" ? null : termIdFromHash(window.location.hash);
  const initialGroupId =
    typeof window === "undefined"
      ? null
      : glossaryGroupFromHash(window.location.hash);
  const pendingScrollId = useRef<string | null>(
    initialTermId
      ? `term-${initialTermId}`
      : initialGroupId
        ? `glossary-${initialGroupId}`
        : null,
  );
  const [scrollToken, setScrollToken] = useState(0);

  const needle = query.trim().toLowerCase();

  const visibleGroups = useMemo(() => {
    if (!needle) return GLOSSARY_GROUPS;
    return GLOSSARY_GROUPS.filter((group) =>
      glossaryTermsInGroup(group).some((term) => termMatches(term, needle)),
    );
  }, [needle]);

  useEffect(() => {
    const applyHash = () => {
      const groupId = glossaryGroupFromHash(window.location.hash);
      const termId = termIdFromHash(window.location.hash);
      if (termId) {
        pendingScrollId.current = `term-${termId}`;
      } else if (groupId) {
        pendingScrollId.current = `glossary-${groupId}`;
      } else {
        return;
      }
      if (groupId) setOpenGroup(groupId);
      setHighlightedTermId(termId);
      setScrollToken((token) => token + 1);
    };
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  // Scroll only after the previous group has closed and the new one opened,
  // otherwise scrollIntoView lands past the intended section.
  useLayoutEffect(() => {
    const targetId = pendingScrollId.current;
    if (!targetId) return;

    let cancelled = false;
    let innerFrame = 0;
    const outerFrame = window.requestAnimationFrame(() => {
      innerFrame = window.requestAnimationFrame(() => {
        if (cancelled) return;
        document.getElementById(targetId)?.scrollIntoView({
          block: "start",
          behavior: "auto",
        });
        pendingScrollId.current = null;
      });
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(outerFrame);
      window.cancelAnimationFrame(innerFrame);
    };
  }, [openGroup, highlightedTermId, scrollToken]);

  const jumpTo = (groupId: string) => {
    setQuery("");
    pendingScrollId.current = `glossary-${groupId}`;
    setOpenGroup(groupId);
    setHighlightedTermId(null);
    setScrollToken((token) => token + 1);
    window.history.replaceState(null, "", glossaryGroupHref(groupId));
  };

  return (
    <section id="glossary" className="scroll-mt-24">
      <h2 className={header2Styles}>Labels & terminology</h2>
      <p className={paragraphStyles}>
        Badges and layer names used on the explorer and in the thesis numbers.
        Jump a group, search, or open one section at a time.
      </p>

      <div className="sticky top-16 z-20 mt-6 rounded-xl border border-line/25 bg-bg/90 p-3 backdrop-blur-md">
        <p className="sr-only">Jump to a label group</p>
        <div className="flex flex-wrap gap-2">
          {GLOSSARY_GROUPS.map((group) => (
            <button
              key={group.id}
              type="button"
              onClick={() => jumpTo(group.id)}
              aria-label={`Jump to ${group.title}`}
              className={[
                "rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                openGroup === group.id && !needle
                  ? "border-accent bg-accent/20 text-accent"
                  : "border-line/35 bg-surface text-muted hover:border-accent/50 hover:text-ink",
              ].join(" ")}
            >
              {group.tag}
            </button>
          ))}
        </div>
        <label className="sr-only" htmlFor="glossary-search">
          Search labels
        </label>
        <input
          id="glossary-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search labels — Grad-CAM, TM90, 2026…"
          className="mt-3 w-full rounded-lg border border-line/35 bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-accent"
        />
      </div>

      <div className="mt-4 space-y-3">
        {visibleGroups.length === 0 ? (
          <p className="px-1 py-6 text-sm text-muted">No labels match that search.</p>
        ) : (
          visibleGroups.map((group) => (
            <GroupPanel
              key={group.id}
              group={group}
              query={needle}
              open={Boolean(needle) || openGroup === group.id}
              highlightedTermId={highlightedTermId}
              onToggle={() =>
                setOpenGroup((current) =>
                  current === group.id && !needle ? null : group.id,
                )
              }
            />
          ))
        )}
      </div>
    </section>
  );
}
